import React from 'react';
import { format } from 'date-fns';

const ApprovalHistory = ({ approvalLogs, approvals }) => {
  // Combine approval logs and approvals, sort by date
  const allHistory = [
    ...(approvalLogs || []).map(log => ({
      ...log,
      type: 'log',
      date: log.approvedAt,
      action: log.action,
      user: log.approver,
      comments: log.remarks
    })),
    ...(approvals || []).map(approval => ({
      ...approval,
      type: 'approval',
      date: approval.approvedAt || approval.createdAt,
      action: approval.status,
      user: approval.approvedBy,
      comments: approval.comments
    }))
  ].sort((a, b) => new Date(a.date) - new Date(b.date));

  if (!allHistory || allHistory.length === 0) {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center">
          <div className="text-gray-400 text-2xl mb-2">📋</div>
          <p className="text-gray-500 text-sm">No approval history yet</p>
        </div>
      </div>
    );
  }

  const getActionIcon = (action) => {
    switch (action) {
      case 'approved':
        return (
          <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'rejected':
        return (
          <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'approved':
        return 'border-green-200 bg-green-50';
      case 'rejected':
        return 'border-red-200 bg-red-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const getActionText = (action) => {
    switch (action) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      default:
        return action;
    }
  };

  return (
    <div className="mt-6">
      <h4 className="font-medium text-gray-900 mb-4 flex items-center">
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Approval History Timeline
      </h4>
      
      <div className="space-y-4">
        {allHistory.map((item, index) => (
          <div key={`${item.type}-${item.id}-${index}`} className="relative">
            {/* Timeline connector */}
            {index < allHistory.length - 1 && (
              <div className="absolute left-4 top-8 w-0.5 h-8 bg-gray-200"></div>
            )}
            
            <div className={`relative flex items-start space-x-4 p-4 rounded-lg border ${getActionColor(item.action)}`}>
              {/* Action Icon */}
              {getActionIcon(item.action)}
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.action === 'approved' 
                        ? 'bg-green-100 text-green-800' 
                        : item.action === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {getActionText(item.action)}
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      by {item.user?.name || 'Unknown User'}
                    </span>
                    <span className="text-sm text-gray-500">
                      ({item.user?.role?.name || 'Unknown Role'})
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {format(new Date(item.date), 'MMM dd, yyyy HH:mm')}
                  </span>
                </div>
                
                {item.comments && (
                  <div className="mt-2 p-3 bg-white rounded border border-gray-200">
                    <p className="text-sm text-gray-700">{item.comments}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ApprovalHistory; 